import type { FC } from 'react';
import { JSPWrapper } from './JSP.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface JSPProps { }

const JSP: FC<JSPProps> = () => (
   <JSPWrapper>
      <header className="entry-header">
         <h1 className="entry-title" itemProp="headline">JSP</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text">
         <p className="text-align-justify">JSP stands for <strong>Java Server Pages</strong>. A JSP page typically consists of HTML tags and JSP tags. It is a sever side technology to create dynamic platform-independent web content. JSP first converts into servlet with the help of the JSP container before processing the client’s request.</p>
         <p className="text-align-justify">JSP technology helps to create web-based applications just like Servlet technology. In other words, it can be thought of as an extension to Servlet because it provides more functionality than servlet. The JSP pages are quite easy to maintain as compared to Servlet because we can separate design and development. In addition, it provides some additional features such as Expression Language, Custom Tags, etc.</p>
         <br/>
         <h4 className="text-align-justify"><strong>Syntax :</strong></h4>
         <ul className="text-align-justify">
            <li><strong>Declaration Tags :</strong>
               <ul>
                  <li>It is used to declare variables.</li>
                  <li>{'<%! Dec var %>'}</li>
               </ul>
            </li>
            <li><strong>Java Scriptlets</strong><strong>–</strong>
               <ul>
                  <li>It allows us to add java code, variables, and expressions.</li>
                  <li>{'<% java code %>'}</li>
               </ul>
            </li>
            <li><strong>JSP Expression –</strong>
               <ul>
                  <li>It evaluates and converts the expression to a string.</li>
                  <li>{'<%= expression %>'}</li>
               </ul>
            </li>
            <li><strong>Comments –</strong>
               <ul>
                  <li>It contains the text that is added for information that has to be ignored.</li>
                  <li>{'<% — Comments %>'}</li>
               </ul>
            </li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Advantages of JSP over Servlet :</strong></h4>
         <ul className="text-align-justify">
            <li><strong>Easy maintenance</strong>
               <ul>
                  <li>JSP is easily managable because we can easily separate our business logic with presentation logic.</li>
               </ul>
            </li>
            <li><strong>Quick development</strong>
               <ul>
                  <li>If the JSP page is modified, we don’t need to recompile and redeploy the project.</li>
               </ul>
            </li>
            <li><strong>Access available to entire APIs of JAVA</strong>
               <ul>
                  <li>JSP have access to the entire family of Java APIs, including the JDBC API to access enterprise databases.</li>
               </ul>
            </li>
            <li><strong>An extended version of Servlet</strong>
               <ul>
                  <li>In addition to features of Servlet, we can use implicit objects, predefined tags, expression language, and Custom tags in JSP.</li>
               </ul>
            </li>
            <li><strong>Reduction in code length</strong>
               <ul>
                  <li>In JSP, we can use many tags such as action tags, JSTL, custom tags, etc. that reduce the code.</li>
               </ul>
            </li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Life cycle of JSP :</strong></h4>
         <ul className="text-align-justify">
            <li>Translation of JSP Page into Servlet by JSP Translator.</li>
            <li>The compilation of Servlet converts it to a class file.</li>
            <li>Classloading i.e. the classloader loads class file.</li>
            <li>Instantiation of a Generated Servlet is done. </li>
            <li>Initialization performed by jspInit() method.</li>
            <li>Request processing performed by jspService() method.</li>
            <li>Destruction performed by jspDestroy() method.</li>
         </ul>
         <br/>
         <h4 className="text-align-justify"><strong>Creating a JSP Page :</strong></h4>
         <ul className="text-align-justify">
            <li>Write some HTML code and save it by .jsp extension. Let’s say we save it as index.jsp. </li>
            <li>Put it in a folder and paste the folder in the web-apps directory in apache tomcat to run the JSP page.</li>
            <li>Follow the directory structure :
               <ul>
                  <li>web-apps folder
                     <ul>
                        <li>WEB_INF folder
                           <ul>
                              <li>classes folder
                                 <ul>
                                    <li>class files</li>
                                 </ul>
                              </li>
                              <li>lib folder</li>
                              <li>web.xml</li>
                           </ul>
                        </li>
                        <li>JSP files</li>
                        <li>Static Resources like HTML, images, etc</li>
                     </ul>
                  </li>
               </ul>
            </li>
            <li>Start the server.</li>
            <li>Deploy on the server.</li>
            <li>Visit the browser by the URL http://localhost:8888/app/index.jsp</li>
         </ul>
         <br/>
         <SyntaxHighlighter language="html" style={dracula} showLineNumbers={true}>
            {
               '<html>\n' +
               '	<head>\n' +
               '		<meta http-equiv="Content-Type" content="text/html;charset=ISO-8859-1">\n' +
               '		<title>Hello World</title>\n' +
               '	</head>\n' +
               '	<body>\n' +
               '		<%= "Hello World!" %>\n' +
               '	</body>\n' +
               '</html>'
            }
         </SyntaxHighlighter>
         <br/>
         <h4 className="text-align-justify"><strong>Creating Custom Tag :</strong></h4>
         <ul className="text-align-justify">
            <li><strong>Create the Tag handler class :</strong>
               <ul>
                  <li>To perform an action at the start or at the end of the tag.</li>
                  <li>Inherit TagSupport class and overriding its method doStartTag().</li>
                  <li>Use the JspWriter class to write data for JSP.</li>
                  <li>The PageContext class provides getOut() method that returns the instance of the JspWriter class. </li>
                  <li>TagSupport class provides an instance of pageContext by default.</li>
               </ul>
            </li>
            <br/>
            <li><strong>Create the TLD file :</strong>
               <ul>
                  <li>In the Tag Library Descriptor file we define tags i.e. it contains information of tag and Tag Hander classes. </li>
                  <li>It must be present inside the WEB-INF directory.</li>
               </ul>
            </li>
            <br/>
            <li><strong>Create the JSP file :</strong>
               <ul>
                  <li>It uses the Custom tag defined in the TLD file.</li>
                  <li>In addition, it is recommended to use the URI name instead of the full path of the TLD file. </li>
                  <li>It uses taglib directive to use the tags defined in the TLD file. </li>
               </ul>
            </li>
         </ul>
         <br/>
         <div style={{backgroundColor: 'lightblue', padding: '10px'}}>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               'public class MyTagHandler extends TagSupport {\n' +
               '  public int doStartTag() throws JspException {\n' +
               '    JspWriter out = pageContext.getOut();\n' +
               '    try {\n' +
               '      System.out.println(Calender.getInstance().getTime());\n' +
               '    } catch (Exception e) {\n' +
               '      System.out.println(e);\n' +
               '    }\n' +
               '    return SKIP_BODY;\n' +
               '  }\n' +
               '}' 
            }
         </SyntaxHighlighter>
         <SyntaxHighlighter language="markup" style={dracula} showLineNumbers={true}>
            {
               '<tag>\n' +
               '	<name>\n' +
               '		Today\n' +
               '	</name>\n' +
               '	<tag-class>\n' +
               '		com.project.MyTagHandler\n' +
               '	</tag-class>\n' +
               '</tag>'
            }
         </SyntaxHighlighter>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               '<%@ taglib uri="WEB-INF/mytags.tld" prefix="m" %>'
            }
         </SyntaxHighlighter>
         </div>
      </div>
   </JSPWrapper>
);

export default JSP;
